import React, { useEffect, useState } from "react";
import UpdateBuyerProductStockTable from "./UpdateBuyerProductStockTable";
import ApiPagination from "@/components/apiPagination/ApiPagination";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchCustomPaginatedQuickReordersQueryOptions } from "@/queryOptions/products/fetchCustomPaginatedQuickReorders";
import { LoaderCircle } from "lucide-react";

interface UpdateBuyerProductStockTableWrapperProps {
  userId: string;
  categories: any[];
  handleStockUpdate: (product: any, newStock: number) => void;
  stockLoading: boolean;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}

const UpdateBuyerProductStockTableWrapper: React.FC<
  UpdateBuyerProductStockTableWrapperProps
> = ({
  userId,
  categories,
  handleStockUpdate,
  stockLoading,
  dialogOpen,
  setDialogOpen,
}) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const filters = React.useMemo(
    () => ({
      user_id: { eq: userId || "" },
    }),
    [userId]
  );

  const {
    data: queryResult,
    isLoading: productsLoading,
    fetchNextPage,
    hasNextPage,
    isSuccess,
    isFetchingNextPage,
  } = useInfiniteQuery(
    fetchCustomPaginatedQuickReordersQueryOptions(filters, 25)
  );

  const currentPage = queryResult?.pages?.[currentPageIndex];
  const totalPages = queryResult?.pages?.length || 0;

  useEffect(() => {
    if (isSuccess && queryResult?.pages?.length === 1 && hasNextPage) {
      fetchNextPage();
    }
  }, [isSuccess, queryResult?.pages?.length, hasNextPage, fetchNextPage]);

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleNext = async () => {
    const nextPageIndex = currentPageIndex + 1;
    if (queryResult?.pages?.[nextPageIndex]) {
      setCurrentPageIndex(nextPageIndex);
      if (!queryResult.pages[nextPageIndex + 1] && hasNextPage) {
        fetchNextPage();
      }
    } else if (hasNextPage) {
      await fetchNextPage();
      setCurrentPageIndex(nextPageIndex);
    }
  };

  if (productsLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoaderCircle className="w-6 h-6 animate-spin mr-4 " />
        Fetching Quick Reorder Orders. This won’t take long!
      </div>
    );
  }


  return (
    <div>
      <UpdateBuyerProductStockTable
        products={currentPage?.items ?? []}
        categories={categories}
        handleStockUpdate={handleStockUpdate}
        stockLoading={stockLoading}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        children={
          <ApiPagination
            currentPageIndex={currentPageIndex}
            totalPages={totalPages}
            hasNextPage={currentPageIndex < (queryResult?.pages?.length || 0) - 1 || hasNextPage}
            isFetching={productsLoading || isFetchingNextPage}
            onPrevious={handlePrevious}
            onNext={handleNext}
            variant="minimal"
          />
        }
      />
    
    </div>
  );
};

export default UpdateBuyerProductStockTableWrapper;
